/**
 * 编解码 wasm 模块的加载与调用（M1-14 / DESIGN.md §6.4）。
 *
 * 这一层只做三件事：加载模块、在线性内存里搬字节、按 `DecodeResult` 的布局读回结果。
 * 所有真正的解码逻辑都在 wasm 里（libpng / mozjpeg）。
 *
 * **不用 wasm-bindgen**：本模块编到 `wasm32-wasip1`（C 库需要 libc），
 * 导出的是 C ABI。接口很窄，手写胶水比引一层生成代码更可控——
 * 代价是结构体偏移要与 Rust 侧手工对齐，所以下面那张偏移表必须与
 * `crates/codecs/src/lib.rs` 的 `DecodeResult` 严格同步，有测试守着。
 */

/** 与 Rust 侧 `ErrorCode` 一一对应。 */
export const CodecError = {
  Ok: 0,
  BadInput: 1,
  NotThisFormat: 2,
  DecodeFailed: 3,
  TooLarge: 4,
  OutOfMemory: 5,
  EncodeFailed: 6,
} as const;

export type CodecErrorCode = (typeof CodecError)[keyof typeof CodecError];

const ERROR_MESSAGES: Readonly<Record<number, string>> = {
  [CodecError.BadInput]: '输入为空、过大，或与声明的尺寸不符',
  [CodecError.NotThisFormat]: '文件头与该格式不匹配',
  [CodecError.DecodeFailed]: '解码失败：文件已损坏或格式不受支持',
  [CodecError.TooLarge]: '图像尺寸超出上限',
  [CodecError.OutOfMemory]: '内存不足',
  [CodecError.EncodeFailed]: '编码失败',
};

export class CodecFailure extends Error {
  constructor(readonly code: number) {
    super(`${ERROR_MESSAGES[code] ?? '未知错误'}（code=${code}）`);
    this.name = 'CodecFailure';
  }
}

/**
 * `DecodeResult` 的字段偏移（字节）。
 *
 * wasm32 下指针与 usize 都是 4 字节，结构体字段按声明顺序紧密排布。
 * **改动 Rust 侧的结构体就必须同步这里**——测试会用一张已知图片校验，
 * 偏移错了会立刻表现为尺寸或长度读成天文数字。
 */
const DECODE_OFFSETS = {
  code: 0,
  width: 4,
  height: 8,
  bitDepth: 12,
  channels: 16,
  pixels: 20,
  pixelsLen: 24,
  icc: 28,
  iccLen: 32,
  exif: 36,
  exifLen: 40,
} as const;

/** 结构体总大小，用于测试断言布局没有走样。 */
export const DECODE_RESULT_SIZE = 44;

const ENCODE_OFFSETS = { code: 0, bytes: 4, bytesLen: 8 } as const;

interface CodecExports {
  readonly memory: WebAssembly.Memory;
  alloc(len: number): number;
  dealloc(ptr: number, len: number): void;
  decode_png(ptr: number, len: number): number;
  decode_jpeg(ptr: number, len: number): number;
  encode_png(
    pixels: number,
    pixelsLen: number,
    width: number,
    height: number,
    bitDepth: number,
    icc: number,
    iccLen: number,
  ): number;
  encode_jpeg(
    pixels: number,
    pixelsLen: number,
    width: number,
    height: number,
    quality: number,
    progressive: number,
    icc: number,
    iccLen: number,
    exif: number,
    exifLen: number,
  ): number;
  free_decode_result(ptr: number): void;
  free_encode_result(ptr: number): void;
  jpeg_is_progressive(ptr: number, len: number): number;
}

/** 解码出来的图像。像素一律是 RGBA（CMYK 源除外，见 `isCmyk`）。 */
export interface DecodedImage {
  readonly width: number;
  readonly height: number;
  /** 每通道位深：8 或 16。 */
  readonly bitDepth: number;
  readonly channels: number;
  /**
   * 像素数据。位深 16 时是小端序的 16 位值（wasm 侧已做字节序转换），
   * 可以直接用 `new Uint16Array(pixels.buffer)` 读。
   */
  readonly pixels: Uint8Array;
  /**
   * 原始 ICC profile 字节，没有则为 null。
   *
   * **不做任何解析**——§2.4 要求把原始 profile 交给上层由 lcms2 处理。
   * 这里哪怕看一眼它的内容都是越界。
   */
  readonly icc: Uint8Array | null;
  /** 原始 EXIF 字节（含 `Exif\0\0` 前缀），没有则为 null。 */
  readonly exif: Uint8Array | null;
}

/**
 * WASI 的最小 stub。
 *
 * 编解码走的是纯内存路径，不会真正读写文件。留这些桩是因为 libc 把它们
 * 静态链进来了，实例化时必须能解析。真被调用到说明有代码走了意料之外的路径，
 * 所以 `proc_exit` 直接抛——静默返回会让问题以"结果莫名其妙"的形式出现。
 */
function wasiStub(): WebAssembly.Imports {
  const ERRNO_BADF = 8;
  const noop = (): number => ERRNO_BADF;
  return {
    wasi_snapshot_preview1: {
      environ_get: noop,
      environ_sizes_get: noop,
      fd_close: noop,
      fd_prestat_get: noop,
      fd_prestat_dir_name: noop,
      fd_seek: noop,
      fd_write: noop,
      proc_exit: (code: number): never => {
        throw new Error(`编解码 wasm 调用了 proc_exit(${code})，这不该发生`);
      },
    },
  };
}

let modulePromise: Promise<WebAssembly.Module> | null = null;

/**
 * 加载 wasm 模块（只编译一次，实例可以有多个）。
 *
 * 分开编译与实例化，是为了让每次解码都能用**全新的实例**——
 * 共用实例时线性内存只增不减，解一张大图之后内存就再也回不去了。
 */
export async function loadCodecModule(url?: string): Promise<WebAssembly.Module> {
  modulePromise ??= (async () => {
    const href = url ?? new URL('../../wasm/codecs/photosmith_codecs.wasm', import.meta.url).href;
    const response = await fetch(href);
    if (!response.ok) {
      throw new Error(`加载编解码 wasm 失败：${response.status} ${response.statusText}`);
    }
    return await WebAssembly.compileStreaming(response).catch(async () => {
      // 某些环境（含部分测试环境）拿不到正确的 MIME，退回非流式编译
      const bytes = await (await fetch(href)).arrayBuffer();
      return await WebAssembly.compile(bytes);
    });
  })();
  return await modulePromise;
}

/** 供测试注入已编译模块，绕过网络加载。 */
export function setCodecModuleForTest(module: Promise<WebAssembly.Module> | null): void {
  modulePromise = module;
}

/** 一个编解码器实例。用完调 `dispose()`。 */
export class CodecInstance {
  readonly #exports: CodecExports;

  private constructor(exports: CodecExports) {
    this.#exports = exports;
  }

  static async create(module?: WebAssembly.Module): Promise<CodecInstance> {
    const compiled = module ?? (await loadCodecModule());
    const instance = await WebAssembly.instantiate(compiled, wasiStub());
    return new CodecInstance(instance.exports as unknown as CodecExports);
  }

  /** 把字节搬进 wasm 线性内存。返回指针，调用方负责释放。 */
  #putBytes(data: Uint8Array): number {
    const ptr = this.#exports.alloc(data.length);
    if (ptr === 0) throw new CodecFailure(CodecError.OutOfMemory);
    new Uint8Array(this.#exports.memory.buffer, ptr, data.length).set(data);
    return ptr;
  }

  /** 从线性内存拷出来。**必须拷贝**：后续任何分配都可能让内存增长、视图失效。 */
  #takeBytes(ptr: number, len: number): Uint8Array | null {
    if (ptr === 0 || len === 0) return null;
    return new Uint8Array(this.#exports.memory.buffer.slice(ptr, ptr + len));
  }

  #readDecodeResult(resultPtr: number): DecodedImage {
    const view = new DataView(this.#exports.memory.buffer);
    const code = view.getUint32(resultPtr + DECODE_OFFSETS.code, true);
    if (code !== CodecError.Ok) {
      this.#exports.free_decode_result(resultPtr);
      throw new CodecFailure(code);
    }

    const image: DecodedImage = {
      width: view.getUint32(resultPtr + DECODE_OFFSETS.width, true),
      height: view.getUint32(resultPtr + DECODE_OFFSETS.height, true),
      bitDepth: view.getUint32(resultPtr + DECODE_OFFSETS.bitDepth, true),
      channels: view.getUint32(resultPtr + DECODE_OFFSETS.channels, true),
      pixels:
        this.#takeBytes(
          view.getUint32(resultPtr + DECODE_OFFSETS.pixels, true),
          view.getUint32(resultPtr + DECODE_OFFSETS.pixelsLen, true),
        ) ?? new Uint8Array(0),
      icc: this.#takeBytes(
        view.getUint32(resultPtr + DECODE_OFFSETS.icc, true),
        view.getUint32(resultPtr + DECODE_OFFSETS.iccLen, true),
      ),
      exif: this.#takeBytes(
        view.getUint32(resultPtr + DECODE_OFFSETS.exif, true),
        view.getUint32(resultPtr + DECODE_OFFSETS.exifLen, true),
      ),
    };
    this.#exports.free_decode_result(resultPtr);
    return image;
  }

  #readEncodeResult(resultPtr: number): Uint8Array {
    const view = new DataView(this.#exports.memory.buffer);
    const code = view.getUint32(resultPtr + ENCODE_OFFSETS.code, true);
    if (code !== CodecError.Ok) {
      this.#exports.free_encode_result(resultPtr);
      throw new CodecFailure(code);
    }
    const bytes =
      this.#takeBytes(
        view.getUint32(resultPtr + ENCODE_OFFSETS.bytes, true),
        view.getUint32(resultPtr + ENCODE_OFFSETS.bytesLen, true),
      ) ?? new Uint8Array(0);
    this.#exports.free_encode_result(resultPtr);
    return bytes;
  }

  decodePng(data: Uint8Array): DecodedImage {
    const ptr = this.#putBytes(data);
    try {
      return this.#readDecodeResult(this.#exports.decode_png(ptr, data.length));
    } finally {
      this.#exports.dealloc(ptr, data.length);
    }
  }

  decodeJpeg(data: Uint8Array): DecodedImage {
    const ptr = this.#putBytes(data);
    try {
      return this.#readDecodeResult(this.#exports.decode_jpeg(ptr, data.length));
    } finally {
      this.#exports.dealloc(ptr, data.length);
    }
  }

  encodePng(options: {
    pixels: Uint8Array;
    width: number;
    height: number;
    bitDepth?: 8 | 16;
    icc?: Uint8Array | null;
  }): Uint8Array {
    const { pixels, width, height } = options;
    const bitDepth = options.bitDepth ?? 8;
    const icc = options.icc ?? null;
    const pixelsPtr = this.#putBytes(pixels);
    const iccPtr = icc === null ? 0 : this.#putBytes(icc);
    try {
      return this.#readEncodeResult(
        this.#exports.encode_png(
          pixelsPtr,
          pixels.length,
          width,
          height,
          bitDepth,
          iccPtr,
          icc?.length ?? 0,
        ),
      );
    } finally {
      this.#exports.dealloc(pixelsPtr, pixels.length);
      if (iccPtr !== 0) this.#exports.dealloc(iccPtr, icc!.length);
    }
  }

  encodeJpeg(options: {
    pixels: Uint8Array;
    width: number;
    height: number;
    quality?: number;
    progressive?: boolean;
    icc?: Uint8Array | null;
    exif?: Uint8Array | null;
  }): Uint8Array {
    const { pixels, width, height } = options;
    const icc = options.icc ?? null;
    const exif = options.exif ?? null;
    const pixelsPtr = this.#putBytes(pixels);
    const iccPtr = icc === null ? 0 : this.#putBytes(icc);
    const exifPtr = exif === null ? 0 : this.#putBytes(exif);
    try {
      return this.#readEncodeResult(
        this.#exports.encode_jpeg(
          pixelsPtr,
          pixels.length,
          width,
          height,
          options.quality ?? 90,
          options.progressive === true ? 1 : 0,
          iccPtr,
          icc?.length ?? 0,
          exifPtr,
          exif?.length ?? 0,
        ),
      );
    } finally {
      this.#exports.dealloc(pixelsPtr, pixels.length);
      if (iccPtr !== 0) this.#exports.dealloc(iccPtr, icc!.length);
      if (exifPtr !== 0) this.#exports.dealloc(exifPtr, exif!.length);
    }
  }

  /** 一张 JPEG 是否为渐进式。不解码，只扫 marker。 */
  isProgressiveJpeg(data: Uint8Array): boolean {
    const ptr = this.#putBytes(data);
    try {
      return this.#exports.jpeg_is_progressive(ptr, data.length) === 1;
    } finally {
      this.#exports.dealloc(ptr, data.length);
    }
  }

  /** 当前线性内存字节数。测试用它检查有没有泄漏。 */
  get memoryBytes(): number {
    return this.#exports.memory.buffer.byteLength;
  }
}
